/*
 * Plan Builder — top-level container.
 *
 * Holds the navigation state (landing → lens 1..5 → diagnostic 0..3 →
 * results), wires up persistence (motr_plan_v1 + motr-lens-v1), loads
 * the published config via usePlanBuilderConfig, and triggers the
 * submission POST when the buyer lands on the results screen.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { recordEvent } from '@shared/api/events';
import {
  createEmptyLens,
  hydrateLens,
  writeLens,
  type SharedLens,
} from '@shared/lens';
import { Landing } from './components/Landing';
import { LensPhase, type LensStep } from './components/LensPhase';
import { DiagnosticPhase, type DiagnosticSection } from './components/DiagnosticPhase';
import { ResultsPage } from './components/ResultsPage';
import { TeamModal } from './components/TeamModal';
import { setAppNavProgress } from '@shared/components/appNavProgress';
import { scoreAll } from './scoring';
import { submitPlan, clearSubmissionId } from './submission';
import { clearLocalState, loadLocalState, saveLocalState } from './state';
import type { DiagnosticAnswers } from './types';
import { usePlanBuilderConfig } from './usePlanBuilderConfig';
import { PlanBuilderConfigContext } from './PlanBuilderConfigContext';
import './PlanBuilder.css';

type Screen =
  | { kind: 'landing' }
  | { kind: 'lens'; step: LensStep }
  | { kind: 'diagnostic'; section: DiagnosticSection }
  | { kind: 'results' };

export default function PlanBuilder() {
  const { config, loading: configLoading } = usePlanBuilderConfig();
  const [screen, setScreen] = useState<Screen>({ kind: 'landing' });
  const [lens, setLens] = useState<SharedLens>(() => createEmptyLens());
  const [wasFromOtherTool, setWasFromOtherTool] = useState(false);
  const [answers, setAnswers] = useState<DiagnosticAnswers>({});
  const [teamModalOpen, setTeamModalOpen] = useState(false);
  const [submissionId, setSubmissionId] = useState<string | null>(null);
  const closeTeamModal = useCallback(() => setTeamModalOpen(false), []);
  const submittedRef = useRef(false);
  // Mount-time timestamp; used to stamp completion_seconds on submission
  // and on the results_shown event for funnel timing.
  const startTimeRef = useRef<number>(Date.now());

  // Mount — hydrate lens (cross-tool) + tool-local state.
  useEffect(() => {
    void recordEvent({ tool_id: 'plan_builder', event_type: 'view' });

    const { lens: hydrated, wasFromOtherTool: fromOther } = hydrateLens('plan_builder');
    setLens(hydrated);
    setWasFromOtherTool(fromOther);

    const local = loadLocalState();
    setAnswers(local.answers);
  }, []);

  // Apply admin var overrides (from config.vars) at the document root,
  // matching the prototype's brand-override behavior.
  useEffect(() => {
    if (!config.vars) return;
    const root = document.documentElement;
    const previous: Array<[string, string]> = [];
    for (const [k, v] of Object.entries(config.vars)) {
      previous.push([k, root.style.getPropertyValue(k)]);
      root.style.setProperty(k, v);
    }
    return () => {
      for (const [k, v] of previous) {
        if (v) root.style.setProperty(k, v);
        else root.style.removeProperty(k);
      }
    };
  }, [config.vars]);

  // Phase-boundary events. Fire once each transition.
  const phaseEventsFiredRef = useRef<Record<string, boolean>>({});
  useEffect(() => {
    const fire = (key: string, eventType: string, data?: Record<string, unknown>) => {
      if (phaseEventsFiredRef.current[key]) return;
      phaseEventsFiredRef.current[key] = true;
      void recordEvent({ tool_id: 'plan_builder', event_type: eventType, data });
    };
    if (screen.kind === 'diagnostic') fire('lens_done', 'phase1_complete');
    if (screen.kind === 'results') fire('done', 'diagnostic_complete');
  }, [screen]);

  // Update the global AppNav progress bar as the buyer moves through
  // diagnostic sections. Reset to null on unmount so other routes see
  // no progress bar.
  useEffect(() => {
    setAppNavProgress(computeTopProgress(screen));
    return () => {
      setAppNavProgress(null);
    };
  }, [screen]);

  // Persist lens to motr-lens-v1 on every change while we're on the
  // lens screens. The Workbench will see the same lens immediately on
  // its next mount.
  const updateLens = (next: SharedLens) => {
    setLens(next);
    writeLens(next, 'plan_builder');
  };

  // Persist diagnostic answers to motr_plan_v1 on every change.
  const updateAnswers = (next: DiagnosticAnswers) => {
    setAnswers(next);
    saveLocalState({ version: 1, answers: next });
  };

  // Submit once on first results-screen render. Subsequent re-renders
  // (e.g., result of state churn from buttons) don't re-fire — the
  // ref guards against it. The API client itself idempotency-protects
  // duplicates via client_submission_id, but we don't want to spam.
  useEffect(() => {
    // Wait for the admin config to resolve (or fail back to defaults)
    // before submitting — otherwise the persisted stage_name would
    // reflect the bundled defaults instead of the admin-customised
    // stage copy.
    if (screen.kind !== 'results' || submittedRef.current || configLoading) return;
    submittedRef.current = true;
    const score = scoreAll(answers, config);
    const completionSeconds = Math.round((Date.now() - startTimeRef.current) / 1000);
    void recordEvent({
      tool_id: 'plan_builder',
      event_type: 'results_shown',
      data: { completion_seconds: completionSeconds, stage: score.stage },
    });
    void submitPlan(lens, answers, score, completionSeconds).then((id) => {
      if (id) setSubmissionId(id);
    });
  }, [screen, answers, lens, config, configLoading]);

  const navigateTo = (next: Screen) => {
    setScreen(next);
    window.scrollTo(0, 0);
  };

  const handleRetake = () => {
    clearLocalState();
    clearSubmissionId();
    setAnswers({});
    setSubmissionId(null);
    submittedRef.current = false;
    navigateTo({ kind: 'landing' });
  };

  return (
    <PlanBuilderConfigContext.Provider value={config}>
      <div className="pb-root">
        <main>
          {screen.kind === 'landing' && (
            <Landing
              onStart={() => navigateTo({ kind: 'lens', step: 1 })}
              onOpenTeamModal={() => setTeamModalOpen(true)}
            />
          )}

          {screen.kind === 'lens' && (
            <LensPhase
              step={screen.step}
              lens={lens}
              wasFromOtherTool={wasFromOtherTool && screen.step === 1}
              onLensChange={updateLens}
              onBack={() => {
                if (screen.step === 1) navigateTo({ kind: 'landing' });
                else
                  navigateTo({
                    kind: 'lens',
                    step: (screen.step - 1) as LensStep,
                  });
              }}
              onNext={() => {
                if (screen.step === 5)
                  navigateTo({ kind: 'diagnostic', section: 0 });
                else
                  navigateTo({
                    kind: 'lens',
                    step: (screen.step + 1) as LensStep,
                  });
              }}
            />
          )}

          {screen.kind === 'diagnostic' && (
            <DiagnosticPhase
              section={screen.section}
              answers={answers}
              lens={lens}
              onAnswerChange={updateAnswers}
              onBack={() => {
                if (screen.section === 0)
                  navigateTo({ kind: 'lens', step: 5 });
                else
                  navigateTo({
                    kind: 'diagnostic',
                    section: (screen.section - 1) as DiagnosticSection,
                  });
              }}
              onNext={() => {
                if (screen.section === 3) navigateTo({ kind: 'results' });
                else
                  navigateTo({
                    kind: 'diagnostic',
                    section: (screen.section + 1) as DiagnosticSection,
                  });
              }}
            />
          )}

          {screen.kind === 'results' && (
            <ResultsPage
              lens={lens}
              answers={answers}
              submissionId={submissionId}
              onRetake={handleRetake}
            />
          )}
        </main>
        <TeamModal open={teamModalOpen} onClose={closeTeamModal} />
      </div>
    </PlanBuilderConfigContext.Provider>
  );
}

function computeTopProgress(
  screen: Screen,
): { label: string; value: number } | null {
  if (screen.kind === 'landing' || screen.kind === 'lens') return null;
  if (screen.kind === 'diagnostic') {
    return {
      label: `Diagnostic · Section ${screen.section + 1} of 4`,
      value: ((screen.section + 1) / 4) * 100,
    };
  }
  return { label: 'Results ready', value: 100 };
}
